/**
 * Transforms an Account Leadsheet 294 V2 trial balance response into a plain
 * normalized shape (arrays per bucket + metadata) that the slice can feed into
 * adapter `setAll` calls. This module performs no adapter mutations — the slice
 * owns all `setAll` / `getInitialState` calls.
 */
import {getReferenceGroupRowLabel} from '../../components/Common/CanvasForm/Bodies/BodyTypes/BodySpecial/AccountLeadsheet294/utils/constants';

/**
 * Returns an empty normalized leadsheet shape.
 * @returns {object}
 */
function createEmptyNormalizedState() {
	return {
		trialBalance: null,
		financialStatements: [],
		accountSubClasses: [],
		glAccounts: [],
		references: [],
		tickmarks: [],
		metadata: null,
		isLoading: false,
		isLoaded: false,
		error: null,
		lastFetched: null
	};
}

/**
 * Flattens financial statements, sub-classes, GLs, references, and tickmarks from the API response.
 * GL ids inside each sub-class preserve the server order.
 * @param {object} apiResponse - Raw trial balance response.
 * @returns {object}
 */
function normalizeGLAccount(gl, referenceItems, tickmarkItems) {
	const refIds = [];
	const tickmarkIds = [];

	for (const ref of gl.references || []) {
		if (ref?.docId === null || ref?.docId === undefined) continue;
		if (!ref.isArchived) continue;
		refIds.push(ref.docId);
		if (!referenceItems.has(ref.docId)) referenceItems.set(ref.docId, ref);
	}

	for (const tm of gl.tickmarks || []) {
		if (tm?.engTagId === null || tm?.engTagId === undefined) continue;
		tickmarkIds.push(tm.engTagId);
		if (!tickmarkItems.has(tm.engTagId)) tickmarkItems.set(tm.engTagId, tm);
	}

	return {
		id: gl.id,
		entity: {...gl, references: refIds, tickmarks: tickmarkIds}
	};
}

function normalizeAccountSubclass(fsId, fs, sc, scIndex, referenceItems, tickmarkItems) {
	const glIds = [];
	const glItems = [];

	for (const gl of sc.glAccounts || []) {
		const normalizedGL = normalizeGLAccount(gl, referenceItems, tickmarkItems);
		glIds.push(normalizedGL.id);
		glItems.push(normalizedGL.entity);
	}

	// Document-grouped responses (viewType:1) use `docId` as the bucket key. The
	// Untested/Incomplete groups have no document, so `docId` is null and `id` is
	// the default 0 for every one of them; fall back to a synthetic per-group key
	// to keep those sub-class/reference entities distinct (a bare `id: 0` would
	// collapse the Untested and Incomplete buckets into one).
	const subClassKey = sc.docId ?? (sc.id ? sc.id : `${fsId}-sc-${scIndex}`);
	// `getReferenceGroupRowLabel` is non-empty ONLY for the docless Untested/Incomplete
	// groups, so it is the safe discriminant for their label; Tested and FS-view
	// sub-classes fall back to `sc.name`.
	const referenceGroupLabel = getReferenceGroupRowLabel(fs.groupByName);
	const subClassName = referenceGroupLabel !== '' ? referenceGroupLabel : sc.name;
	const subClassHasAdjCurr = (sc.glAccounts || []).some((gl) => gl?.hasAdjCurr === true);

	return {
		id: subClassKey,
		entity: {...sc, id: subClassKey, name: subClassName, hasAdjCurr: subClassHasAdjCurr, glAccounts: glIds},
		glItems
	};
}

function normalizeFinancialStatement(fs, fsIndex, referenceItems, tickmarkItems) {
	const fsId = `fs-${fs.fsTypeId}-${fsIndex}`;
	const subClassIds = [];
	const subClassItems = [];
	const glItems = [];

	for (let scIndex = 0; scIndex < (fs.accSubClassesOrRef || []).length; scIndex++) {
		const normalizedSubclass = normalizeAccountSubclass(fsId, fs, fs.accSubClassesOrRef[scIndex], scIndex, referenceItems, tickmarkItems);
		subClassIds.push(normalizedSubclass.id);
		subClassItems.push(normalizedSubclass.entity);
		glItems.push(...normalizedSubclass.glItems);
	}

	return {
		id: fsId,
		entity: {
			id: fsId,
			fsTypeId: fs.fsTypeId,
			groupByName: fs.groupByName,
			accountSubClasses: subClassIds
		},
		subClassItems,
		glItems
	};
}

function flattenLeadsheetEntities(apiResponse) {
	const fsIds = [];
	const fsItems = [];
	const subClassItems = [];
	const glItems = [];
	const referenceItems = new Map();
	const tickmarkItems = new Map();

	for (let fsIndex = 0; fsIndex < (apiResponse.leadsheet || []).length; fsIndex++) {
		const normalizedFS = normalizeFinancialStatement(apiResponse.leadsheet[fsIndex], fsIndex, referenceItems, tickmarkItems);
		fsIds.push(normalizedFS.id);
		fsItems.push(normalizedFS.entity);
		subClassItems.push(...normalizedFS.subClassItems);
		glItems.push(...normalizedFS.glItems);
	}

	return {
		financialStatementIds: fsIds,
		financialStatements: fsItems,
		accountSubClasses: subClassItems,
		glAccounts: glItems,
		references: Array.from(referenceItems.values()),
		tickmarks: Array.from(tickmarkItems.values())
	};
}

/**
 * Builds a lean trial balance entity from the raw API response: every TB-level
 * scalar plus `financialStatements` as the FS adapter id list. The nested
 * `financialStatements` array of full objects is dropped because that data
 * lives in the sibling `fsData` / `accountSubClassData` / `glAccountData`
 * buckets after normalization.
 *
 * Note: `leadsheet` is destructured but unused to exclude it from the spread
 * operator. This preserves V8 fast-properties optimization by avoiding the
 * `delete` operation which triggers dictionary mode deoptimization.
 *
 * @param {object} apiResponse - Raw trial balance response.
 * @param {Array} financialStatementIds - Ordered FS adapter ids.
 * @returns {object}
 */
function buildTrialBalanceEntity(apiResponse, financialStatementIds) {
	const {leadsheet: _rawLeadsheet, ...tbScalars} = apiResponse;
	return {
		...tbScalars,
		financialStatements: financialStatementIds
	};
}

/**
 * Builds period and pagination metadata from the API response.
 * @param {object} apiResponse - Raw trial balance response.
 * @returns {object}
 */
function buildMetadata(apiResponse) {
	return {
		currentPeriodDate: apiResponse.currDate,
		comparativePeriodDate: apiResponse.compDate,
		secondComparativePeriodDate: apiResponse.secondDate,
		page: apiResponse.page,
		totalPages: apiResponse.pages,
		totalEntityCount: apiResponse.totalEntities
	};
}

/**
 * Normalizes one trial balance API response into plain bucket arrays plus metadata.
 * The slice converts these arrays into EntityState via adapter `setAll`. The
 * returned `trialBalance` is the lean TB entity that the slice writes into
 * `tbData` — its `financialStatements` field holds only the FS adapter ids.
 * @param {object} apiResponse - Raw trial balance response.
 * @returns {object}
 */
export function normalizeLeadsheetResponse(apiResponse) {
	if (!apiResponse?.leadsheet) {
		return createEmptyNormalizedState();
	}

	const flat = flattenLeadsheetEntities(apiResponse);

	return {
		trialBalance: buildTrialBalanceEntity(apiResponse, flat.financialStatementIds),
		financialStatements: flat.financialStatements,
		accountSubClasses: flat.accountSubClasses,
		glAccounts: flat.glAccounts,
		references: flat.references,
		tickmarks: flat.tickmarks,
		metadata: buildMetadata(apiResponse),
		isLoading: false,
		isLoaded: true,
		error: null,
		lastFetched: new Date().toISOString()
	};
}
